import {
  CREATIVE_LEARNING_RESOURCE,
  EXPERIMENT_RESOURCE,
  OUTCOME_RESOURCE,
  type LifecycleResource,
} from '@studio/shared';
import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../../shell/api.js';
import { useResourceEvents } from '../../shell/stream.js';

/**
 * The Learn stage, kept live.
 *
 * Everything on this stage is assembled by one read, so reconciling it is one read again
 * rather than a patch: an Outcome recorded in another tab, a Learning enriched by the
 * extraction Job, a state that moved - all of them arrive on the shared stream and the
 * stage is re-read. Nothing here waits for the operator to reload the page.
 */
export interface OpenLifecycle {
  readonly stage: LifecycleResource | null;
  readonly error: string | null;
  refresh(): void;
  apply(stage: LifecycleResource): void;
}

export function useLifecycle(experimentId: string): OpenLifecycle {
  const [stage, setStage] = useState<LifecycleResource | null>(null);
  const [error, setError] = useState<string | null>(null);

  const read = useCallback(async () => {
    try {
      setStage(await api.readLifecycle(experimentId));
      setError(null);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Этот Experiment не удалось прочитать.');
    }
  }, [experimentId]);

  useEffect(() => {
    void read();
  }, [read]);

  useResourceEvents((event) => {
    const mine =
      event.resource === EXPERIMENT_RESOURCE
        ? event.id === experimentId
        : event.resource === OUTCOME_RESOURCE || event.resource === CREATIVE_LEARNING_RESOURCE;
    if (mine) void read();
  });

  return {
    stage,
    error,
    refresh: () => void read(),
    apply: (next) => setStage(next),
  };
}
